import { loadSlot, saveSlot } from '../save/storage';
import type { SlotId, StorageLike } from '../save/types';
import { formatRupiah } from '../sim/economy';
import {
  getRoomDefinitions,
  getRoomVisualDefinition,
  Simulation
} from '../sim/Simulation';
import { formatGameDate } from '../sim/time';
import type { BuildFailReason } from '../sim/types';

export const FAIL_MESSAGES: Record<BuildFailReason, string> = {
  UNKNOWN_ROOM_TYPE: 'Tipe ruangan tidak dikenal!',
  INVALID_COORDINATES: 'Koordinat tidak valid!',
  OUT_OF_BOUNDS: 'Ruangan melewati batas gedung!',
  RESERVED_ENTRANCE: 'Pintu masuk lobi tidak boleh ditutup!',
  RESERVED_STAIRWELL: 'Kolom ini dicadangkan untuk tangga!',
  OVERLAPPING: 'Petak sudah terisi ruangan lain!',
  NO_STRUCTURAL_SUPPORT: 'Lantai atas memerlukan ruangan penyangga di bawahnya!',
  INSUFFICIENT_FUNDS: 'Saldo kas tidak mencukupi!'
};

export class HUD {
  private container: HTMLElement;
  private moneyEl!: HTMLElement;
  private repEl!: HTMLElement;
  private dateEl!: HTMLElement;
  private patientsCountEl!: HTMLElement;
  private pauseBtn!: HTMLButtonElement;
  private speedBtns: Map<number, HTMLButtonElement> = new Map();
  private buildBtns: Map<string, HTMLButtonElement> = new Map();
  private cancelBtn!: HTMLButtonElement;
  private menuBtn!: HTMLButtonElement;
  private saveIndicatorEl!: HTMLElement;
  private toastContainer!: HTMLElement;

  private selectedRoomTypeId: string | null = null;
  private onSelectRoomCb?: (typeId: string | null) => void;
  private onStateLoadedCb?: () => void;
  private onNewGameCb?: () => void;

  private lastSavedGameMs: number = 0;

  constructor(
    private sim: Simulation,
    private storage: StorageLike
  ) {
    const root = document.getElementById('hud-root');
    if (!root) {
      throw new Error('Elemen #hud-root tidak ditemukan di DOM!');
    }
    this.container = root;
    this.render();
    this.attachEvents();
    this.update();
  }

  public setSimulation(newSim: Simulation): void {
    this.sim = newSim;
    this.update();
  }

  public onSelectRoom(cb: (typeId: string | null) => void): void {
    this.onSelectRoomCb = cb;
  }

  public onStateLoaded(cb: () => void): void {
    this.onStateLoadedCb = cb;
  }

  public onNewGame(cb: () => void): void {
    this.onNewGameCb = cb;
  }

  public getSelectedRoomTypeId(): string | null {
    return this.selectedRoomTypeId;
  }

  public clearSelection(): void {
    this.selectedRoomTypeId = null;
    this.buildBtns.forEach((btn) => btn.classList.remove('selected'));
    this.cancelBtn.classList.remove('visible');
    if (this.onSelectRoomCb) {
      this.onSelectRoomCb(null);
    }
  }

  public showToast(message: string, type: 'info' | 'success' | 'error' = 'info'): void {
    const toast = document.createElement('div');
    toast.className = `hud-toast ${type}`;
    toast.textContent = message;
    this.toastContainer.appendChild(toast);
    setTimeout(() => {
      if (toast.parentElement) {
        toast.parentElement.removeChild(toast);
      }
    }, 2800);
  }

  public updateSaveIndicator(text: string): void {
    this.saveIndicatorEl.textContent = text;
  }

  public setLastSavedGameMs(ms: number): void {
    this.lastSavedGameMs = ms;
  }

  public update(): void {
    const state = this.sim.getState();
    this.moneyEl.textContent = formatRupiah(state.money);
    this.dateEl.textContent = formatGameDate(state.totalGameMs);
    this.repEl.textContent = `★ ${state.reputation}/100`;
    this.patientsCountEl.textContent = `${state.patients.length} Pasien`;

    if (state.isPaused) {
      this.pauseBtn.textContent = '▶ Lanjut';
      this.pauseBtn.classList.add('paused');
    } else {
      this.pauseBtn.textContent = '⏸ Pause';
      this.pauseBtn.classList.remove('paused');
    }

    this.speedBtns.forEach((btn, speed) => {
      if (state.speedMultiplier === speed) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
  }

  private render(): void {
    this.container.innerHTML = `
      <header class="hud-header">
        <div class="hud-stat-group">
          <div class="hud-stat">
            <span class="hud-stat-label">Kas Klinik</span>
            <span class="hud-stat-value money" id="hud-money">Rp 0</span>
          </div>
          <div class="hud-stat">
            <span class="hud-stat-label">Reputasi</span>
            <span class="hud-stat-value rep" id="hud-rep" style="color:#f1c40f;">★ 50/100</span>
          </div>
          <div class="hud-stat">
            <span class="hud-stat-label">Kunjungan</span>
            <span class="hud-stat-value" id="hud-patients-count" style="color:#74b9ff;">0 Pasien</span>
          </div>
          <div class="hud-stat">
            <span class="hud-stat-label">Waktu</span>
            <span class="hud-stat-value" id="hud-date">Hari 1 — 08:00</span>
          </div>
        </div>
        <div class="hud-controls">
          <button class="hud-btn pause-btn" id="hud-pause">⏸ Pause</button>
          <button class="hud-btn" data-speed="1">1x</button>
          <button class="hud-btn" data-speed="2">2x</button>
          <button class="hud-btn" data-speed="5">5x</button>
          <button class="hud-btn menu-btn" id="hud-menu">☰ Menu</button>
        </div>
      </header>

      <div class="hud-toast-container" id="hud-toasts"></div>

      <div class="hud-footer-container">
        <div class="hud-save-indicator" id="hud-save-indicator">Belum ada save</div>
        <footer class="hud-footer">
          <div id="hud-build-buttons" style="display:flex; gap:10px;"></div>
          <button class="hud-cancel-btn" id="hud-cancel">✕ Batal (Esc)</button>
        </footer>
      </div>
    `;

    this.moneyEl = document.getElementById('hud-money')!;
    this.repEl = document.getElementById('hud-rep')!;
    this.patientsCountEl = document.getElementById('hud-patients-count')!;
    this.dateEl = document.getElementById('hud-date')!;
    this.pauseBtn = document.getElementById('hud-pause') as HTMLButtonElement;
    this.cancelBtn = document.getElementById('hud-cancel') as HTMLButtonElement;
    this.menuBtn = document.getElementById('hud-menu') as HTMLButtonElement;
    this.saveIndicatorEl = document.getElementById('hud-save-indicator')!;
    this.toastContainer = document.getElementById('hud-toasts')!;

    const speedButtons = this.container.querySelectorAll<HTMLButtonElement>('button[data-speed]');
    speedButtons.forEach((btn) => {
      const speed = Number(btn.getAttribute('data-speed'));
      this.speedBtns.set(speed, btn);
    });

    const buildContainer = document.getElementById('hud-build-buttons')!;
    const roomDefs = getRoomDefinitions();
    roomDefs.forEach((def) => {
      const visual = getRoomVisualDefinition(def.id);
      const name = visual ? visual.name : def.id;
      const color = visual ? visual.color : '#7f8c8d';

      const btn = document.createElement('button');
      btn.className = 'hud-build-btn';
      btn.innerHTML = `
        <span class="hud-build-color-badge" style="background:${color}"></span>
        <div class="hud-build-info">
          <span class="hud-build-name">${name} (${def.width} petak)</span>
          <span class="hud-build-cost">${formatRupiah(def.cost)}</span>
        </div>
      `;
      btn.addEventListener('click', () => {
        if (this.selectedRoomTypeId === def.id) {
          this.clearSelection();
        } else {
          this.selectedRoomTypeId = def.id;
          this.buildBtns.forEach((b) => b.classList.remove('selected'));
          btn.classList.add('selected');
          this.cancelBtn.classList.add('visible');
          if (this.onSelectRoomCb) {
            this.onSelectRoomCb(def.id);
          }
        }
      });
      buildContainer.appendChild(btn);
      this.buildBtns.set(def.id, btn);
    });
  }

  private attachEvents(): void {
    this.pauseBtn.addEventListener('click', () => {
      this.sim.dispatch({ type: 'TOGGLE_PAUSE' });
      this.update();
    });

    this.speedBtns.forEach((btn, speed) => {
      btn.addEventListener('click', () => {
        this.sim.dispatch({ type: 'SET_SPEED', speed });
        this.update();
      });
    });

    this.cancelBtn.addEventListener('click', () => {
      this.clearSelection();
    });

    this.menuBtn.addEventListener('click', () => {
      this.openMenuModal();
    });

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const modal = document.querySelector('.hud-modal-overlay');
        if (modal) {
          modal.parentElement?.removeChild(modal);
        } else {
          this.clearSelection();
        }
      }
    });
  }

  public openMenuModal(): void {
    // Pastikan tidak ada modal ganda
    const existing = document.querySelector('.hud-modal-overlay');
    if (existing) existing.parentElement?.removeChild(existing);

    const overlay = document.createElement('div');
    overlay.className = 'hud-modal-overlay';

    const manualLoad = loadSlot(this.storage, 'manual');
    const autoLoad = loadSlot(this.storage, 'autosave');

    const manualExists = manualLoad.ok || manualLoad.reason !== 'NO_SAVE';
    const autoExists = autoLoad.ok || autoLoad.reason !== 'NO_SAVE';

    const manualDesc = manualLoad.ok
      ? `${formatGameDate(manualLoad.state.totalGameMs)} • ${formatRupiah(manualLoad.state.money)}`
      : manualLoad.reason === 'NO_SAVE'
        ? 'Kosong'
        : `⚠️ Data tidak valid (${manualLoad.reason})`;

    const autoDesc = autoLoad.ok
      ? `${formatGameDate(autoLoad.state.totalGameMs)} • ${formatRupiah(autoLoad.state.money)}`
      : autoLoad.reason === 'NO_SAVE'
        ? 'Kosong'
        : `⚠️ Data tidak valid (${autoLoad.reason})`;

    overlay.innerHTML = `
      <div class="hud-modal">
        <div class="hud-modal-header">
          <span class="hud-modal-title">Menu Game</span>
          <button class="hud-modal-close" id="hud-modal-close">&times;</button>
        </div>
        <div class="hud-modal-body">
          <div class="hud-slot-card">
            <div class="hud-slot-info">
              <span class="hud-slot-name">Slot Manual</span>
              <span class="hud-slot-details">${manualDesc}</span>
            </div>
            <div class="hud-slot-actions">
              <button class="hud-btn" id="hud-save-manual-btn">💾 Simpan</button>
              <button class="hud-btn" id="hud-load-manual-btn" ${!manualExists ? 'disabled style="opacity:0.5;cursor:not-allowed;"' : ''}>📂 Muat</button>
            </div>
          </div>

          <div class="hud-slot-card">
            <div class="hud-slot-info">
              <span class="hud-slot-name">Slot Auto-save</span>
              <span class="hud-slot-details">${autoDesc}</span>
            </div>
            <div class="hud-slot-actions">
              <button class="hud-btn" id="hud-load-auto-btn" ${!autoExists ? 'disabled style="opacity:0.5;cursor:not-allowed;"' : ''}>📂 Muat</button>
            </div>
          </div>

          <hr style="border:none; border-top:1px solid rgba(255,255,255,0.1); margin: 4px 0;" />

          <button class="hud-danger-btn" id="hud-new-game-btn">🔄 Mulai Game Baru</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    overlay.querySelector('#hud-modal-close')?.addEventListener('click', () => {
      document.body.removeChild(overlay);
    });

    overlay.querySelector('#hud-save-manual-btn')?.addEventListener('click', () => {
      const saveRes = saveSlot(this.storage, 'manual', this.sim.getSnapshot());
      if (saveRes.ok) {
        this.lastSavedGameMs = this.sim.getState().totalGameMs;
        const dateStr = formatGameDate(this.lastSavedGameMs);
        this.updateSaveIndicator(`Tersimpan: ${dateStr} (manual)`);
        this.showToast('Game berhasil disimpan ke Slot Manual!', 'success');
        document.body.removeChild(overlay);
      } else {
        const msg = saveRes.reason === 'QUOTA_EXCEEDED' ? 'Penyimpanan penuh!' : 'Penyimpanan browser tidak tersedia!';
        this.showToast(msg, 'error');
      }
    });

    overlay.querySelector('#hud-load-manual-btn')?.addEventListener('click', () => {
      this.confirmAndLoadSlot('manual', overlay);
    });

    overlay.querySelector('#hud-load-auto-btn')?.addEventListener('click', () => {
      this.confirmAndLoadSlot('autosave', overlay);
    });

    overlay.querySelector('#hud-new-game-btn')?.addEventListener('click', () => {
      this.confirmNewGame(overlay);
    });
  }

  private confirmAndLoadSlot(slot: SlotId, currentModal: HTMLElement): void {
    const hasUnsavedProgress = this.sim.getState().totalGameMs > this.lastSavedGameMs;
    if (hasUnsavedProgress) {
      this.openConfirmationDialog(
        'Ada progres game yang belum disimpan sejak simpan terakhir. Tetap muat slot ini?',
        () => {
          this.executeLoad(slot, currentModal);
        }
      );
    } else {
      this.executeLoad(slot, currentModal);
    }
  }

  private executeLoad(slot: SlotId, modalToClose: HTMLElement): void {
    const result = loadSlot(this.storage, slot);
    if (result.ok) {
      this.sim.loadState(result.state);
      this.lastSavedGameMs = result.state.totalGameMs;
      const dateStr = formatGameDate(result.state.totalGameMs);
      this.updateSaveIndicator(`Tersimpan: ${dateStr} (${slot === 'manual' ? 'manual' : 'otomatis'})`);
      this.showToast(`Game berhasil dimuat dari ${slot}!`, 'success');
      if (modalToClose.parentElement) {
        modalToClose.parentElement.removeChild(modalToClose);
      }
      if (this.onStateLoadedCb) {
        this.onStateLoadedCb();
      }
    } else {
      const reasonMessages: Record<string, string> = {
        NO_SAVE: 'Belum ada data save pada slot ini.',
        STORAGE_UNAVAILABLE: 'Penyimpanan browser tidak dapat diakses.',
        CORRUPT_JSON: 'File save rusak (JSON tidak valid).',
        INVALID_ENVELOPE: 'Struktur save tidak valid.',
        NEWER_VERSION: 'Save berasal dari versi game yang lebih baru!',
        MIGRATION_FAILED: 'Gagal memigrasikan save lama.',
        INVALID_STATE: 'Data save tidak lolos validasi aturan gedung.',
        UNKNOWN_CONTENT_ID: 'Data save merujuk konten yang tidak terdaftar.'
      };
      const msg = reasonMessages[result.reason] || 'Gagal memuat save game.';
      this.showToast(msg, 'error');
    }
  }

  private confirmNewGame(currentModal: HTMLElement): void {
    this.openConfirmationDialog(
      'Mulai game baru? Progres yang belum disimpan akan hilang (slot lama tidak dihapus sampai Anda menyimpan lagi).',
      () => {
        if (currentModal.parentElement) {
          currentModal.parentElement.removeChild(currentModal);
        }
        if (this.onNewGameCb) {
          this.onNewGameCb();
        }
      }
    );
  }

  private openConfirmationDialog(message: string, onConfirm: () => void): void {
    const confirmOverlay = document.createElement('div');
    confirmOverlay.className = 'hud-modal-overlay';
    confirmOverlay.style.zIndex = '1100';

    confirmOverlay.innerHTML = `
      <div class="hud-modal" style="width:400px;">
        <div class="hud-modal-header">
          <span class="hud-modal-title">Konfirmasi</span>
          <button class="hud-modal-close" id="hud-confirm-close">&times;</button>
        </div>
        <div class="hud-modal-body">
          <p style="font-size:14px; line-height:1.5; color:#dcdde1;">${message}</p>
          <div style="display:flex; justify-content:flex-end; gap:10px; margin-top:8px;">
            <button class="hud-btn" id="hud-confirm-cancel">Batal</button>
            <button class="hud-danger-btn" id="hud-confirm-ok">Lanjutkan</button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(confirmOverlay);

    const closeConfirm = () => {
      if (confirmOverlay.parentElement) {
        confirmOverlay.parentElement.removeChild(confirmOverlay);
      }
    };

    confirmOverlay.querySelector('#hud-confirm-close')?.addEventListener('click', closeConfirm);
    confirmOverlay.querySelector('#hud-confirm-cancel')?.addEventListener('click', closeConfirm);
    confirmOverlay.querySelector('#hud-confirm-ok')?.addEventListener('click', () => {
      closeConfirm();
      onConfirm();
    });
  }
}
